// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import type { SeriesKey } from '../types';
import { SERIES_COLOUR, seriesLabel } from '../colours';
import { lineChart, type LineSeries } from '../charts';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatNumber, formatPercent } from '../utils/format';
import { viewHead } from './shared';

const SERIES: SeriesKey[] = ['NSW-boy', 'NSW-girl', 'QLD-boy', 'QLD-girl'];

export function renderDiversity(root: HTMLElement, ctx: ViewContext): void {
  const div = ctx.data.agg.diversity;
  const latest = ctx.data.meta.latestYear;

  const legend = SERIES.map(
    (sk) => `<span class="legend-item"><span class="legend-swatch line" style="background:${SERIES_COLOUR[sk]}"></span>${escapeHtml(seriesLabel(sk))}</span>`,
  ).join('');

  // headline contrast (NSW boys)
  const b = div['NSW-boy'] || [];
  const first = b[0];
  const last = b[b.length - 1];

  root.innerHTML = `
    ${viewHead(
      'Diversity: the great fragmentation',
      `A single year's top-10 list hides the biggest change in Australian naming: the top names simply do not dominate the way they once did. ${glossaryTerm(
        'concentration',
        'Concentration',
      )} — the share of babies who got a top-10 name — has fallen steadily for boys and girls in both states.`,
    )}

    <div class="callout">
      Among NSW boys, the #1 name went to <strong>${first ? formatNumber(first.no1Count) : ''}</strong> babies in
      ${first ? first.year : ''} (${first ? formatPercent(first.top10Share, 0) : ''} of the top 100 shared the top-10 names) —
      by ${latest} the #1 reached <strong>${last ? formatNumber(last.no1Count) : ''}</strong>, with just
      ${last ? formatPercent(last.top10Share, 0) : ''} in the top 10. Fewer babies, spread across far more names.
    </div>

    <div class="panel">
      <div class="panel-title">Top-10 concentration over time</div>
      <div class="panel-sub">Share of all top-100 babies (each year) who were given one of the ten most popular names. Falling = names spreading out.</div>
      <div class="chart-scroll" data-role="conc"></div>
      <div class="legend">${legend}</div>
    </div>

    <div class="panel">
      <div class="panel-title">How many babies got the #1 name</div>
      <div class="panel-sub">The raw count given the single most popular name each year. Even as the population grew, the number fell — the clearest sign the top name means less.</div>
      <div class="chart-scroll" data-role="no1"></div>
      <div class="legend">${legend}</div>
    </div>
  `;

  const concSeries: LineSeries[] = SERIES.map((sk) => ({
    id: sk,
    label: seriesLabel(sk),
    colour: SERIES_COLOUR[sk],
    points: (div[sk] || []).map((d) => [d.year, d.top10Share] as [number, number]),
  }));
  const no1Series: LineSeries[] = SERIES.map((sk) => ({
    id: sk,
    label: seriesLabel(sk),
    colour: SERIES_COLOUR[sk],
    points: (div[sk] || []).map((d) => [d.year, d.no1Count] as [number, number]),
  }));

  const xDomain: [number, number] = [1952, latest];
  (root.querySelector('[data-role="conc"]') as HTMLElement).appendChild(
    lineChart(concSeries, { width: 1000, height: 380, xDomain, yDomain: [0, 60], yTickVals: [0, 15, 30, 45, 60], yFmt: (v) => `${Math.round(v)}%`, valueNoun: '', endLabels: false }),
  );
  (root.querySelector('[data-role="no1"]') as HTMLElement).appendChild(
    lineChart(no1Series, { width: 1000, height: 380, xDomain, valueNoun: 'babies', yFmt: (v) => formatNumber(v), endLabels: false }),
  );
}
